import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, Calendar, Clock, Users, Video, ExternalLink, XIcon, EditIcon, Trash2Icon } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { coffeeData, Coffee } from '../components/coffee/coffeeData';
import AlgaCoffeeSummaryWidget from '../components/coffee/widgets/AlgaCoffeeSummaryWidget';
import AlgaCoffeeTranscriptWidget from '../components/coffee/widgets/AlgaCoffeeTranscriptWidget';
import AlgaCoffeeNotesWidget from '../components/coffee/widgets/AlgaCoffeeNotesWidget';
import EditCoffeeModal from '../components/coffee/EditCoffeeModal';
const CoffeeDetailPage = () => {
  const {
    coffeeId
  } = useParams<{
    coffeeId: string;
  }>();
  const navigate = useNavigate();
  const [coffee, setCoffee] = useState<Coffee | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'info' | 'discussion' | 'participants'>('info');
  const [showEditModal, setShowEditModal] = useState(false);
  useEffect(() => {
    if (coffeeId) {
      const foundCoffee = coffeeData.find(c => c.id === coffeeId);
      if (foundCoffee) {
        setCoffee(foundCoffee);
      } else {
        // Coffee not found, redirect to coffee list
        navigate('/coffee');
      }
    }
  }, [coffeeId, navigate]);
  if (!coffee) {
    return <div className="flex h-screen bg-gray-100 items-center justify-center" data-id="element-2168">
        <p data-id="element-2169">Caricamento in corso...</p>
      </div>;
  }
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const isAdmin = coffee.type === 'admin';
  const now = new Date();
  const coffeeDate = new Date(coffee.date);
  const isPast = coffeeDate < now;
  const isJoined = coffee.participants.some(p => p.id === 'current-user');
  const hasParticipated = isPast && isJoined;
  const isHost = coffee.host.id === 'current-user';
  // Platform logos
  const getPlatformLogo = (platform: string) => {
    switch (platform) {
      case 'Google Meet':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/hawwkzWf3TnSE8Q31Hrx5c/images.png';
      case 'Microsoft Teams':
        return 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c9/Microsoft_Office_Teams_%282018%E2%80%93present%29.svg/2203px-Microsoft_Office_Teams_%282018%E2%80%93present%29.svg.png';
      case 'Zoom':
        return 'https://uploadthingy.s3.us-west-1.amazonaws.com/a6M2ckhEkmcVgkXUVkifgg/zoom_logo_icon_147196.png';
      default:
        return null;
    }
  };
  const handleUpdateCoffee = (updatedCoffee: Partial<Coffee>) => {
    if (coffee) {
      // In a real app, this would make an API call
      // For now, we'll just update the local state
      setCoffee({
        ...coffee,
        ...updatedCoffee
      });
      // Show a success message
      alert('Evento aggiornato con successo!');
    }
  };
  const handleDeleteCoffee = () => {
    // In a real app, this would make an API call
    // For now, we'll just redirect to the coffee list
    navigate('/coffee');
    // Show a success message
    alert('Evento eliminato con successo!');
  };
  const renderContent = () => {
    switch (activeTab) {
      case 'info':
        return <div className="grid grid-cols-1 md:grid-cols-4 gap-6" data-id="element-2170">
            <div className="md:col-span-3" data-id="element-2171">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-2172">
                <div className="p-6" data-id="element-2173">
                  <h2 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-2174">
                    Informazioni sull'evento
                  </h2>
                  <p className="text-gray-600 mb-6" data-id="element-2175">{coffee.description}</p>
                  <h3 className="text-md font-medium text-gray-800 mb-2" data-id="element-2176">
                    Temi trattati
                  </h3>
                  <div className="flex flex-wrap gap-2 mb-6" data-id="element-2177">
                    {coffee.topics.map((topic, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm" data-id="element-2178">
                        {topic}
                      </span>)}
                  </div>
                  {isPast ? hasParticipated ? <div className="bg-custom-green/10 rounded-lg p-4 text-center" data-id="element-2179">
                        <p className="text-sm text-custom-green font-medium" data-id="element-2180">
                          Hai partecipato a questo evento
                        </p>
                      </div> : <div className="bg-gray-100 rounded-lg p-4 text-center" data-id="element-2181">
                        <p className="text-sm text-gray-500" data-id="element-2182">
                          Evento terminato
                        </p>
                      </div> : <button className="w-full flex items-center justify-center py-3 bg-custom-green text-white rounded-lg hover:bg-custom-green/90 transition-colors font-medium" data-id="element-2183">
                      <ExternalLink size={18} className="mr-2" data-id="element-2184" />
                      Partecipa all'evento
                    </button>}
                </div>
              </div>
              {hasParticipated && <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-2185">
                  <AlgaCoffeeSummaryWidget coffeeTitle={coffee.title} coffeeHost={coffee.host.name} coffeeTopics={coffee.topics} data-id="element-2186" />
                  <AlgaCoffeeNotesWidget coffeeHost={coffee.host.name} data-id="element-2187" />
                </div>}
            </div>
            <div className="md:col-span-1" data-id="element-2188">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-2189">
                <div className="p-4 border-b border-gray-200" data-id="element-2190">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2191">
                    Dettagli dell'evento
                  </h2>
                </div>
                <div className="p-4" data-id="element-2192">
                  <div className="space-y-3" data-id="element-2193">
                    <div className="flex items-center text-gray-600" data-id="element-2194">
                      <Calendar size={18} className="mr-3 text-custom-green" data-id="element-2195" />
                      <span data-id="element-2196">{formatDate(coffee.date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2197">
                      <Clock size={18} className="mr-3 text-custom-green" data-id="element-2198" />
                      <span data-id="element-2199">
                        {formatTime(coffee.date)} - {formatTime(coffee.endTime)}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2200">
                      {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-5 h-5 mr-3" data-id="element-2201" /> : <Video size={18} className="mr-3 text-custom-green" data-id="element-2202" />}
                      <span data-id="element-2203">{coffee.platform}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6" data-id="element-2204">
                <div className="p-4 border-b border-gray-200" data-id="element-2205">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2206">
                    Host
                  </h2>
                </div>
                <div className="p-4" data-id="element-2207">
                  <div className="flex items-center" data-id="element-2208">
                    <div className={`w-12 h-12 rounded-full overflow-hidden mr-3 flex items-center justify-center ${isAdmin ? 'bg-green-600' : 'bg-white'}`} data-id="element-2209">
                      {isAdmin ? <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-7 h-7 object-contain brightness-0 invert" data-id="element-2210" /> : <img src={coffee.host.avatar} alt={coffee.host.name} className="w-full h-full object-cover" data-id="element-2211" />}
                    </div>
                    <div data-id="element-2212">
                      <p className="font-medium text-gray-800" data-id="element-2213">
                        {coffee.host.name}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-2214">
                        {isAdmin ? 'Greenback Team' : 'Community Member'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2215">
                <div className="p-4 border-b border-gray-200" data-id="element-2216">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2217">
                    Partecipanti
                  </h2>
                </div>
                <div className="p-4" data-id="element-2218">
                  <div className="flex items-center mb-3" data-id="element-2219">
                    <Users size={18} className="text-custom-green mr-2" data-id="element-2220" />
                    <span className="text-sm text-gray-500" data-id="element-2221">
                      {coffee.participants.length} partecipanti
                    </span>
                  </div>
                  <div className="space-y-3" data-id="element-2222">
                    {coffee.participants.map(participant => <div key={participant.id} className="flex items-center" data-id="element-2223">
                        <div className="w-8 h-8 rounded-full overflow-hidden mr-3" data-id="element-2224">
                          <img src={participant.avatar} alt={participant.name} className="w-full h-full object-cover" data-id="element-2225" />
                        </div>
                        <p className="text-sm text-gray-700" data-id="element-2226">
                          {participant.name}
                        </p>
                        {participant.id === 'current-user' && <span className="ml-auto text-xs bg-custom-green/10 text-custom-green px-2 py-0.5 rounded-full" data-id="element-2227">
                            Tu
                          </span>}
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
          </div>;
      case 'discussion':
        return <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2228">
            <div className="md:col-span-2" data-id="element-2229">
              {hasParticipated ? <AlgaCoffeeTranscriptWidget coffeeHost={coffee.host.name} coffeeTitle={coffee.title} data-id="element-2230" /> : <div className="bg-white rounded-xl border border-gray-200 p-6 text-center" data-id="element-2231">
                  <p className="text-gray-500" data-id="element-2232">
                    {isPast ? 'Non hai partecipato a questo evento, quindi la trascrizione non è disponibile.' : "La trascrizione sarà disponibile dopo l'evento."}
                  </p>
                </div>}
            </div>
            <div className="md:col-span-1" data-id="element-2233">
              {hasParticipated && <AlgaCoffeeNotesWidget coffeeHost={coffee.host.name} data-id="element-2234" />}
            </div>
          </div>;
      case 'participants':
        return <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-2235">
            <div className="md:col-span-2" data-id="element-2236">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2237">
                <div className="p-4 border-b border-gray-200" data-id="element-2238">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2239">
                    Tutti i partecipanti
                  </h2>
                </div>
                <div className="p-6" data-id="element-2240">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2241">
                    {coffee.participants.map(participant => <div key={participant.id} className="flex items-center p-3 border border-gray-200 rounded-lg" data-id="element-2242">
                        <div className="w-10 h-10 rounded-full overflow-hidden mr-3" data-id="element-2243">
                          <img src={participant.avatar} alt={participant.name} className="w-full h-full object-cover" data-id="element-2244" />
                        </div>
                        <div data-id="element-2245">
                          <p className="font-medium text-gray-800" data-id="element-2246">
                            {participant.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-2247">
                            {participant.id === 'current-user' ? 'Tu' : 'Partecipante'}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </div>
              </div>
            </div>
            <div className="md:col-span-1" data-id="element-2248">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-2249">
                <div className="p-4 border-b border-gray-200" data-id="element-2250">
                  <h2 className="text-base font-semibold text-gray-800" data-id="element-2251">
                    Informazioni sull'evento
                  </h2>
                </div>
                <div className="p-4" data-id="element-2252">
                  <div className="space-y-3" data-id="element-2253">
                    <div className="flex items-center text-gray-600" data-id="element-2254">
                      <Calendar size={18} className="mr-3 text-gray-500" data-id="element-2255" />
                      <span data-id="element-2256">{formatDate(coffee.date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2257">
                      <Clock size={18} className="mr-3 text-gray-500" data-id="element-2258" />
                      <span data-id="element-2259">
                        {formatTime(coffee.date)} - {formatTime(coffee.endTime)}
                      </span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2260">
                      {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-5 h-5 mr-3" data-id="element-2261" /> : <Video size={18} className="mr-3 text-gray-500" data-id="element-2262" />}
                      <span data-id="element-2263">{coffee.platform}</span>
                    </div>
                    <div className="flex items-center text-gray-600" data-id="element-2264">
                      <Users size={18} className="mr-3 text-gray-500" data-id="element-2265" />
                      <span data-id="element-2266">{coffee.participants.length} partecipanti</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="flex h-screen bg-gray-100" data-id="element-2267">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden ${sidebarOpen ? 'block' : 'hidden'}`} data-id="element-2268">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} data-id="element-2269"></div>
        <div className="relative flex-1 flex flex-col w-2/3 h-full bg-black" data-id="element-2270">
          <div className="absolute top-0 right-0 -mr-12 pt-2" data-id="element-2271">
            <button type="button" className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" onClick={() => setSidebarOpen(false)} data-id="element-2272">
              <span className="sr-only" data-id="element-2273">Close sidebar</span>
              <XIcon className="h-6 w-6 text-white" aria-hidden="true" data-id="element-2274" />
            </button>
          </div>
          <div className="flex-1 h-full overflow-y-auto" data-id="element-2275">
            <Sidebar activePage="community" data-id="element-2276" />
          </div>
        </div>
      </div>
      {/* Static sidebar for desktop */}
      <div className="hidden md:flex md:flex-shrink-0" data-id="element-2277">
        <Sidebar activePage="community" data-id="element-2278" />
      </div>
      <div className="flex flex-col flex-1 overflow-hidden" data-id="element-2279">
        <Topbar onMenuClick={() => setSidebarOpen(true)} data-id="element-2280" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pt-0 md:pt-0 bg-gray-100" data-id="element-2281">
          <div className="pt-4 md:pt-6 max-w-6xl mx-auto" data-id="element-2282">
            {/* Back button */}
            <div className="mb-6" data-id="element-2283">
              <button onClick={() => navigate('/coffee')} className="flex items-center text-gray-600 hover:text-gray-800" data-id="element-2284">
                <ArrowLeftIcon size={18} className="mr-2" data-id="element-2285" />
                <span data-id="element-2286">Torna agli Eventi</span>
              </button>
            </div>
            {/* Coffee Header */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6 overflow-hidden" data-id="element-2287">
              <div className="p-6" data-id="element-2288">
                <div className="flex flex-col sm:flex-row items-start gap-4" data-id="element-2289">
                  <div className={`w-full sm:w-[100px] h-[100px] rounded-full overflow-hidden flex-shrink-0 flex items-center justify-center border-2 ${isAdmin ? 'bg-green-600 border-green-600' : 'bg-gray-100 border-gray-200'}`} data-id="element-2290">
                    {isAdmin ? <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback" className="w-12 h-12 object-contain brightness-0 invert" data-id="element-2291" /> : <img src={coffee.host.avatar} alt={coffee.host.name} className="w-full h-full object-cover" data-id="element-2292" />}
                  </div>
                  <div className="flex-1" data-id="element-2293">
                    <div className="flex justify-between items-start" data-id="element-2294">
                      <h1 className="text-xl font-semibold text-gray-800 mb-2" data-id="element-2295">
                        {coffee.title}
                      </h1>
                      {isHost && !isPast && <div className="flex space-x-2" data-id="element-2296">
                          <button onClick={() => setShowEditModal(true)} className="flex items-center text-gray-600 hover:text-gray-800 px-2 py-1 rounded-md hover:bg-gray-100" data-id="element-2297">
                            <EditIcon size={16} className="mr-1" data-id="element-2298" />
                            <span className="text-sm" data-id="element-2299">Modifica</span>
                          </button>
                          <button onClick={() => {
                        if (window.confirm('Sei sicuro di voler eliminare questo evento?')) {
                          handleDeleteCoffee();
                        }
                      }} className="flex items-center text-red-600 hover:text-red-700 px-2 py-1 rounded-md hover:bg-red-50" data-id="element-2300">
                            <Trash2Icon size={16} className="mr-1" data-id="element-2301" />
                            <span className="text-sm" data-id="element-2302">Elimina</span>
                          </button>
                        </div>}
                    </div>
                    <p className="text-gray-600 mb-4 text-sm" data-id="element-2303">
                      Ospitato da {coffee.host.name} • {formatDate(coffee.date)}
                      {isHost && <span className="ml-2 text-blue-600 text-xs font-medium" data-id="element-2304">
                          (Sei l'host)
                        </span>}
                    </p>
                    <div className="flex flex-wrap items-center gap-4 sm:gap-6" data-id="element-2305">
                      <div className="flex items-center text-gray-500" data-id="element-2306">
                        <Clock size={16} className="mr-1.5" data-id="element-2307" />
                        <span className="text-sm" data-id="element-2308">
                          {formatTime(coffee.date)} -{' '}
                          {formatTime(coffee.endTime)}
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500" data-id="element-2309">
                        <Users size={16} className="mr-1.5" data-id="element-2310" />
                        <span className="text-sm" data-id="element-2311">
                          {coffee.participants.length} partecipanti
                        </span>
                      </div>
                      <div className="flex items-center text-gray-500" data-id="element-2312">
                        {getPlatformLogo(coffee.platform) ? <img src={getPlatformLogo(coffee.platform)} alt={coffee.platform} className="w-4 h-4 mr-1.5" data-id="element-2313" /> : <Video size={16} className="mr-1.5" data-id="element-2314" />}
                        <span className="text-sm" data-id="element-2315">{coffee.platform}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Tab Navigation */}
              <div className="border-t border-gray-200 px-6 overflow-x-auto" data-id="element-2316">
                <nav className="flex space-x-8" data-id="element-2317">
                  <button onClick={() => setActiveTab('info')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'info' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2318">
                    Informazioni
                  </button>
                  <button onClick={() => setActiveTab('discussion')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'discussion' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2319">
                    Discussione
                  </button>
                  <button onClick={() => setActiveTab('participants')} className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${activeTab === 'participants' ? 'border-custom-green text-custom-green' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-2320">
                    Partecipanti
                  </button>
                </nav>
              </div>
            </div>
            {/* Content area */}
            {renderContent()}
          </div>
        </main>
      </div>
      {/* Edit Coffee Modal */}
      {showEditModal && coffee && <EditCoffeeModal isOpen={showEditModal} onClose={() => setShowEditModal(false)} coffee={coffee} onUpdate={handleUpdateCoffee} onDelete={handleDeleteCoffee} data-id="element-2321" />}
    </div>;
};
export default CoffeeDetailPage;